import { BookOpen, ChevronDown, CircleHelp, TrendingDown, Wallet } from 'lucide-react';
import { lightFor, treasuryMetrics, type TreasuryReport } from '@odd/domain';
import './TreasuryGuide.css';

const n = (value: number) => value.toLocaleString('es-ES', { maximumFractionDigits: 1 });
const months = (value: number) => `${n(value)} ${value === 1 ? 'mes' : 'meses'}`;
const sample = treasuryMetrics({
  bancos: 8000,
  caja: 2000,
  proveedores: -1000,
  gastos12m: 72000,
  impagados: 6000,
});
const tones = { rojo: 'bad', ambar: 'warn', verde: 'ok' } as const;

function Thresholds({
  limits,
  inverse = false,
}: {
  limits: { amber: number; red: number };
  inverse?: boolean;
}) {
  return (
    <ul
      className="treasury-guide-thresholds"
      aria-label={inverse ? 'Colores de cobertura' : 'Colores de morosidad'}
    >
      <li className="treasury-guide-tone--bad">
        <span>Rojo</span>
        <strong>{inverse ? `≤ ${months(limits.red)}` : `≥ ${n(limits.red)} %`}</strong>
      </li>
      <li className="treasury-guide-tone--warn">
        <span>Ámbar</span>
        <strong>
          {inverse
            ? `> ${n(limits.red)} y ≤ ${months(limits.amber)}`
            : `≥ ${n(limits.amber)} y < ${n(limits.red)} %`}
        </strong>
      </li>
      <li className="treasury-guide-tone--ok">
        <span>Verde</span>
        <strong>{inverse ? `> ${months(limits.amber)}` : `< ${n(limits.amber)} %`}</strong>
      </li>
    </ul>
  );
}

export function TreasuryGuide({ thresholds }: { thresholds?: TreasuryReport['thresholds'] }) {
  const coverageTone = thresholds ? tones[lightFor(sample.f1!, { ...thresholds.F1, inv: true })] : 'neutral';
  const debtTone = thresholds ? tones[lightFor(sample.f2!, { ...thresholds.F2, inv: false })] : 'neutral';
  return (
    <details className="treasury-guide">
      <summary>
        <BookOpen size={19} strokeWidth={1.5} aria-hidden="true" />
        <span>
          <strong>Cómo se calcula</strong>
          <small>Entiende la cobertura y la morosidad de un vistazo</small>
        </span>
        <ChevronDown size={17} aria-hidden="true" />
      </summary>
      <div className="treasury-guide-content">
        <p className="treasury-guide-intro">
          Dos preguntas para leer las finanzas de cada comunidad. Los ejemplos son orientativos; los colores
          usan los límites del informe.
        </p>
        <div className="treasury-guide-grid">
          <section className="treasury-guide-metric" aria-label="Cálculo de cobertura de caja">
            <div className="treasury-guide-label">
              <Wallet size={16} aria-hidden="true" /> Cobertura de caja
            </div>
            <h3>¿Para cuántos meses alcanza?</h3>
            <p>
              El dinero disponible, comparado con lo que se gasta en un mes. Cuantos menos meses cubra, más
              atención necesita.
            </p>
            <div
              className="treasury-guide-formula"
              role="img"
              aria-label="Disponible dividido por el gasto mensual: meses de cobertura"
            >
              <span>Disponible</span>
              <b aria-hidden="true">÷</b>
              <span>Gasto mensual</span>
              <b aria-hidden="true">=</b>
              <strong>Meses</strong>
            </div>
            <div className={`treasury-guide-example treasury-guide-tone--${coverageTone}`}>
              <div className="treasury-guide-example-heading">
                <small>Un ejemplo</small>
                <strong>{months(sample.f1!)}</strong>
              </div>
              <p>9.000 € disponibles ÷ 6.000 € de gasto al mes</p>
              <figure
                className="treasury-guide-runway"
                aria-label="El dinero cubre un mes completo y la mitad del siguiente"
              >
                {[100, 50, 0].map((fill, i) => (
                  <div key={i}>
                    <div>
                      <i style={{ width: `${fill}%` }} />
                    </div>
                    <span>Mes {i + 1}</span>
                  </div>
                ))}
              </figure>
              <dl className="treasury-guide-breakdown">
                <div>
                  <dt>Disponible</dt>
                  <dd>8.000 € bancos + 2.000 € caja − 1.000 € de deuda a proveedores</dd>
                </div>
                <div>
                  <dt>Gasto mensual</dt>
                  <dd>72.000 € de gastos de los últimos 12 meses ÷ 12</dd>
                </div>
              </dl>
            </div>
            {thresholds && <Thresholds limits={thresholds.F1} inverse />}
            <p className="treasury-guide-note">
              Si el disponible es negativo, la cobertura siempre es roja, aunque no haya gasto registrado.
            </p>
          </section>
          <section className="treasury-guide-metric" aria-label="Cálculo de morosidad">
            <div className="treasury-guide-label">
              <TrendingDown size={16} aria-hidden="true" /> Índice de morosidad
            </div>
            <h3>¿Cuánto pesan los impagados?</h3>
            <p>
              Lo que deben los vecinos, comparado con el gasto de un año. Cuanto mayor sea el porcentaje, más
              atención necesita.
            </p>
            <div
              className="treasury-guide-formula"
              role="img"
              aria-label="Impagados divididos por el gasto anual, por cien: porcentaje de morosidad"
            >
              <span>Impagados</span>
              <b aria-hidden="true">÷</b>
              <span>Gasto anual</span>
              <b aria-hidden="true">× 100 =</b>
              <strong>%</strong>
            </div>
            <div className={`treasury-guide-example treasury-guide-tone--${debtTone}`}>
              <div className="treasury-guide-example-heading">
                <small>La misma comunidad</small>
                <strong>{n(sample.f2!)} %</strong>
              </div>
              <p>6.000 € impagados ÷ 72.000 € de gasto anual × 100</p>
              <figure
                className="treasury-guide-debt"
                aria-label={`Los impagados equivalen al ${n(sample.f2!)} % del gasto anual`}
              >
                <svg viewBox="0 0 56 56" aria-hidden="true">
                  <circle className="treasury-guide-ring-track" cx="28" cy="28" r="22" />
                  <circle
                    className="treasury-guide-ring-value"
                    cx="28"
                    cy="28"
                    r="22"
                    pathLength="100"
                    strokeDasharray={`${sample.f2} ${100 - sample.f2!}`}
                    transform="rotate(-90 28 28)"
                  />
                </svg>
                <figcaption>
                  Por cada 100 € de gasto anual,
                  <br />
                  <strong>{n(sample.f2!)} € pendientes de cobro.</strong>
                </figcaption>
              </figure>
              <p className="treasury-guide-example-note">
                Se toma el gasto de los últimos 12 meses como referencia del presupuesto de la comunidad.
              </p>
            </div>
            {thresholds && <Thresholds limits={thresholds.F2} />}
            <p className="treasury-guide-note">
              El porcentaje compara impagados con gastos. No indica qué porcentaje de vecinos debe dinero.
            </p>
          </section>
        </div>
        {!thresholds && (
          <p className="treasury-guide-note" role="status">
            Los límites de color estarán disponibles cuando se cargue el informe.
          </p>
        )}
        <div className="treasury-guide-caveats">
          <CircleHelp size={17} aria-hidden="true" />
          <div>
            <strong>Qué significa «—»</strong>
            <p>
              Sin gastos en los últimos 12 meses no hay base para calcular los índices, salvo la cobertura con
              disponible negativo. Si no hay impagados, la morosidad también aparece como «—».
            </p>
          </div>
        </div>
        <p className="treasury-guide-source">
          Fuente: saldos de Gesfincas de la foto seleccionada. Un saldo negativo de proveedores es deuda; los
          anticipos a su favor no se añaden al disponible. El saldo de administración no interviene. Los
          límites se ajustan en Administración → Umbrales.
        </p>
      </div>
    </details>
  );
}
